import cx from 'classnames';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { FunctionComponent, useEffect, useMemo } from 'react';

import useIsMobile from '../../../hooks/use-is-mobile';
import useToken from '../../../hooks/use-token';
import { fetchFaqs } from '../../../services/faqs';
import { fetchBrands } from '../../../services/vehicles';
import { allSavedCarsAtom } from '../../../state/vehicle';
import getUserSavedCars from '../../../utils/react-query/query-options/get-user-saved-cars';
import Reviews from '../../modules/reviews';
import ValueProposition from '../../modules/value-proposition';
import { useAppContext } from '../../providers/app-context';
import Curve from '../../ui/curve';
import CategoriesList from '../categories/categories-list';
import LazyClientWrapper from '../../ui/lazy-wrapper';

import AboutUs from './about-us';
import Banner from './banner';
import Categories from './categories';
import Questions from './questions';
import HomeSchema from './seo';
import getLasEntries from '../../../utils/react-query/query-options/get-last-entries';
import getLastVehicles from '../../../utils/react-query/query-options/get-last-vehicles';
import LastEntries from '../v2/vehicle/last-entries';
import LastVehicles from '../v2/vehicle/last-vehicles';
import { fetchGroups } from '../../../services/groups';
import Button from '../../ui/button';
import useLinkProps from '../../../hooks/use-link-props';
import useTranslation from 'next-translate/useTranslation';
import BrandList from '../vehicle/brands/brand-list';
import PartsCloud from './parts-cloud';

const TopBrands = dynamic(() => import('./top-brands'));
const TrustBox = dynamic(() => import('../../modules/trustpilot/new'), { ssr: false });

const Home: FunctionComponent<TPageHome> = ({ groupsSort }) => {
  const getLink = useLinkProps();
  const { locale = 'en', route, push } = useRouter();
  const { tenantConfig, brand } = useAppContext();
  const isMobile = useIsMobile();
  const csrftoken = useToken();
  const {
    features: { isBrandEnabled }, detailedPrice
  } = useAppContext();
  const isUK = process.env.NEXT_PUBLIC_HOST?.includes('.co.uk');
  const isNewInternalLinkingAvailable = isUK || locale === 'en' || locale === 'pt';
  const { t } = useTranslation();
  const [{ data: brands }, { data: faqs }, { data: groups }] = useQueries({
    queries: [
      {
        queryKey: ['brands', locale, false],
        queryFn: ({ queryKey: [, locale, top] }) => fetchBrands({ locale, top: true }),
        staleTime: 10000,
        keepPreviousData: true,
        enabled: isBrandEnabled,
      },
      {
        queryKey: ['faqs', locale, 'home'],
        queryFn: ({ queryKey: [, locale, slug] }) =>
          fetchFaqs({ locale, slug }),
        staleTime: 10000,
        keepPreviousData: true,
      },
      {
        queryKey: ['groups', locale, false],
        queryFn: ({ queryKey: [, locale, top] }) =>
          fetchGroups({ locale, top: true }),
        staleTime: 10000,
        keepPreviousData: true,
      }
    ],
  });

  const { data: savedVehicles, isLoading: isVehiclesLoading } = useQuery(
    ...getUserSavedCars({ locale, csrftoken }),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: 'always',
      refetchInterval: 60000,
      refetchOnMount: true,
      staleTime: 10000,
    }
  );

  const { data: lastEntries } = useQuery(
    ...getLasEntries({ locale, detailed_prices: !!detailedPrice, page: 'home' }),
    {
      keepPreviousData: true,
      enabled: isNewInternalLinkingAvailable
    }
  );

  const { data: lastVehicles } = useQuery(
    ...getLastVehicles({ locale, page: 'home' }),
    {
      keepPreviousData: true,
      enabled: isNewInternalLinkingAvailable
    }
  );

  const brandList = useMemo(() => {
    if (brands && brands.children) {
      return brands.children?.filter((b) => b.logo_url && b.logo_white_url);
    }

    return [];
  }, [brands]);

  const defaultisOpenValue = useMemo(() => {
    return !isMobile;
  }, [isMobile]);

  const [, setAllSavedCars] = useAtom(allSavedCarsAtom);

  useEffect(() => {
    setAllSavedCars(savedVehicles);
  }, [savedVehicles, setAllSavedCars]);

  return (
    <main>
      <HomeSchema faqsForSchema={faqs} />
      <Banner />
      <div className='h-[60px] container bg-white mx-auto flex items-center justify-center'>
        <LazyClientWrapper minHeight="60px" rootMargin="300px">
          <TrustBox />
        </LazyClientWrapper>
      </div>
      <ValueProposition variant="SECONDARY" isHome={true} showTitle={false} />
      <Curve className="h-4 lg:h-6 scale-y text-neutral-10 " />
      <Categories groups={groups} />
      <PartsCloud groups={groups} groupsSort={groupsSort} />
      {!(isNewInternalLinkingAvailable && (lastEntries && !!lastEntries.length)) && <AboutUs />}
      {isNewInternalLinkingAvailable && lastEntries && !!lastEntries.length && (
        <>
          <Curve className="h-4 lg:h-6 scale-y-invert lg:scale-x-invert text-neutral-10 " />
          <LastEntries products={lastEntries} />
          <Curve className="h-4 lg:h-6 scale-y text-neutral-10 " />
        </>
      )}
      <TopBrands brands={brandList} />
      <div className='container mx-auto w-full flex items-center justify-center py-4'>
        <Button
          variant="ROUNDED"
          onClick={() => {
            const link = getLink('brands', {});
            push(link.href, link.as, { shallow: true });
          }}
          className={cx(
            'flex flex-row border-b border-neutral-30 items-center justify-center gap-2  !px-6 !py-3 text-white !bg-neutral-80 hover:!bg-neutral-50',
          )}
          isFullWidth={false}
          disableHeight={true}
        >
          <span className="font-bold text-[18px]">
            {t('brands:searchAllBrands')}
          </span>
        </Button>
      </div>
      {lastVehicles && !!lastVehicles.length && <>
        <Curve className="h-4 lg:h-6 scale-y-invert lg:scale-x-invert text-neutral-10 " />
        <LastVehicles products={lastVehicles} />
        <Curve className="h-4 lg:h-6 scale-y text-neutral-10 " />
      </>}
      {(isNewInternalLinkingAvailable && (lastEntries && !!lastEntries.length)) && <AboutUs backgroundColor={'bg-white'} showCurve={false} />}
      <Questions faqs={faqs} />
      <LazyClientWrapper minHeight="500px" rootMargin="500px">
        <Reviews />
      </LazyClientWrapper>
    </main>
  );
};

export default Home;
